import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  eventConnectorApi,
  workflowApi,
  type EventConnector,
  type EventConnectorProvider,
  type EventConnectorDirection,
} from '@/lib/api';
import {
  Network, Plus, Circle, ChevronRight, Trash2, FlaskConical,
  ArrowDownToLine, ArrowUpFromLine, ArrowLeftRight, Webhook,
  Cpu, Cloud, Mail, Zap, Cable,
} from 'lucide-react';
import { Spinner } from '@/components/ui/Spinner';
import { toast } from '@/components/ui/toast';
import { PageGuide } from '@/components/PageGuide';
import styles from './_auth.event-connectors.module.css';

export const Route = createFileRoute('/_auth/event-connectors/')({ component: EventConnectorsPage });

// ── Provider metadata ──────────────────────────────────────────────────────────

const PROVIDER_META: Record<EventConnectorProvider, { label: string; icon: React.ReactNode; color: string }> = {
  GOOGLE_PUBSUB:     { label: 'Google Pub/Sub',    icon: <Cloud size={14} />,   color: '#4285f4' },
  KAFKA:             { label: 'Apache Kafka',       icon: <Cpu size={14} />,     color: '#ff6b35' },
  AWS_SQS:           { label: 'AWS SQS',            icon: <Mail size={14} />,    color: '#ff9900' },
  AWS_SNS:           { label: 'AWS SNS',            icon: <Zap size={14} />,     color: '#ff9900' },
  AWS_EVENTBRIDGE:   { label: 'AWS EventBridge',    icon: <Cable size={14} />,   color: '#ff9900' },
  AZURE_SERVICE_BUS: { label: 'Azure Service Bus',  icon: <Cloud size={14} />,   color: '#0078d4' },
  WEBHOOK:           { label: 'Webhook',             icon: <Webhook size={14} />, color: '#6366f1' },
};

const DIRECTION_META: Record<EventConnectorDirection, { label: string; icon: React.ReactNode; color: string }> = {
  INBOUND:      { label: 'Inbound',       icon: <ArrowDownToLine size={12} />,  color: '#10b981' },
  OUTBOUND:     { label: 'Outbound',      icon: <ArrowUpFromLine size={12} />,  color: '#f59e0b' },
  BIDIRECTIONAL:{ label: 'Bidirectional', icon: <ArrowLeftRight size={12} />,   color: '#8b5cf6' },
};

// ── Per-provider config field definitions ──────────────────────────────────────

type FieldType = 'text' | 'password' | 'url' | 'toggle' | 'brokers';

interface ConfigField {
  key: string;
  label: string;
  type: FieldType;
  placeholder?: string;
  hint?: string;
  required?: boolean;
}

const PROVIDER_FIELDS: Record<EventConnectorProvider, ConfigField[]> = {
  GOOGLE_PUBSUB: [
    { key: 'projectId',        label: 'GCP Project ID',            type: 'text',     placeholder: 'my-gcp-project',                              required: true  },
    { key: 'topicName',        label: 'Topic Name',                type: 'text',     placeholder: 'my-topic',                                    required: true  },
    { key: 'subscriptionName', label: 'Subscription Name',         type: 'text',     placeholder: 'my-subscription (inbound only)',               required: false },
    { key: 'serviceAccountKey',label: 'Service Account Key',       type: 'password', placeholder: '{{ secrets.GCP_SA_KEY }} or paste JSON',
      hint: 'Use {{ secrets.NAME }} to reference a stored secret. Leave blank to use Application Default Credentials.', required: false },
  ],
  KAFKA: [
    { key: 'brokers',       label: 'Broker Addresses',   type: 'brokers',  placeholder: 'localhost:9092, broker2:9092',   required: true  },
    { key: 'topic',         label: 'Topic',              type: 'text',     placeholder: 'my-events-topic',               required: true  },
    { key: 'groupId',       label: 'Consumer Group ID',  type: 'text',     placeholder: 'enska-consumer-group',          required: false },
    { key: 'ssl',           label: 'TLS / SSL',          type: 'toggle',                                                  required: false },
    { key: 'saslMechanism', label: 'SASL Mechanism',     type: 'text',     placeholder: 'plain or scram-sha-256',        required: false },
    { key: 'saslUsername',  label: 'SASL Username',      type: 'text',     placeholder: 'kafka-user',                    required: false },
    { key: 'saslPassword',  label: 'SASL Password',      type: 'password', placeholder: '{{ secrets.KAFKA_PASSWORD }}',  required: false },
  ],
  AWS_SQS: [
    { key: 'region',          label: 'AWS Region',        type: 'text',     placeholder: 'us-east-1',                                     required: true },
    { key: 'queueUrl',        label: 'Queue URL',         type: 'url',      placeholder: 'https://sqs.us-east-1.amazonaws.com/123456/my-queue', required: true },
    { key: 'accessKeyId',     label: 'Access Key ID',     type: 'text',     placeholder: 'AKIAIOSFODNN7EXAMPLE',                          required: true },
    { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password', placeholder: '{{ secrets.AWS_SECRET_KEY }}',                  required: true },
  ],
  AWS_SNS: [
    { key: 'region',          label: 'AWS Region',        type: 'text',     placeholder: 'us-east-1',                                     required: true },
    { key: 'topicArn',        label: 'Topic ARN',         type: 'text',     placeholder: 'arn:aws:sns:us-east-1:123456789:my-topic',       required: true },
    { key: 'accessKeyId',     label: 'Access Key ID',     type: 'text',     placeholder: 'AKIAIOSFODNN7EXAMPLE',                          required: true },
    { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password', placeholder: '{{ secrets.AWS_SECRET_KEY }}',                  required: true },
  ],
  AWS_EVENTBRIDGE: [
    { key: 'region',          label: 'AWS Region',        type: 'text',     placeholder: 'us-east-1',       required: true  },
    { key: 'eventBusName',    label: 'Event Bus Name',    type: 'text',     placeholder: 'default',         required: false },
    { key: 'source',          label: 'Source',            type: 'text',     placeholder: 'enska.ai',        required: false },
    { key: 'detailType',      label: 'Detail Type',       type: 'text',     placeholder: 'ConnectorEvent',  required: false },
    { key: 'accessKeyId',     label: 'Access Key ID',     type: 'text',     placeholder: 'AKIAIOSFODNN7EXAMPLE',           required: true },
    { key: 'secretAccessKey', label: 'Secret Access Key', type: 'password', placeholder: '{{ secrets.AWS_SECRET_KEY }}',  required: true },
  ],
  AZURE_SERVICE_BUS: [
    { key: 'connectionString', label: 'Connection String',   type: 'password', placeholder: '{{ secrets.AZURE_SB_CONN }} or Endpoint=sb://…',
      hint: 'Use {{ secrets.NAME }} to reference a stored secret.', required: true  },
    { key: 'queueOrTopicName', label: 'Queue or Topic Name', type: 'text',     placeholder: 'my-queue',                  required: true  },
    { key: 'subscriptionName', label: 'Subscription Name',   type: 'text',     placeholder: 'my-subscription (topics only, optional)', required: false },
  ],
  WEBHOOK: [
    { key: 'targetUrl', label: 'Target URL (outbound)', type: 'url', placeholder: 'https://hooks.example.com/ingest',
      hint: 'Required for OUTBOUND direction — the URL events are POSTed to. Leave blank for INBOUND-only.', required: false },
  ],
};

const SENSITIVE_KEYS = new Set(['serviceAccountKey', 'secretAccessKey', 'saslPassword', 'connectionString']);

function buildConfig(fields: ConfigField[], values: Record<string, string | boolean>): Record<string, unknown> {
  const config: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.type === 'toggle') {
      if (values[f.key]) config[f.key] = true;
    } else if (f.type === 'brokers') {
      const raw = (values[f.key] as string) ?? '';
      const list = raw.split(',').map((b) => b.trim()).filter(Boolean);
      if (list.length) config[f.key] = list;
    } else {
      const v = ((values[f.key] as string) ?? '').trim();
      if (v) config[f.key] = v;
    }
  }
  return config;
}

// ── Main page ──────────────────────────────────────────────────────────────────

export default function EventConnectorsPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [testingId, setTestingId]   = useState<string | null>(null);
  const [selected, setSelected]     = useState<EventConnector | null>(null);

  const { data: connectors = [], isLoading } = useQuery({
    queryKey: ['event-connectors'],
    queryFn: eventConnectorApi.list,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) => eventConnectorApi.update(id, { active }),
    onSuccess: (_, { active }) => {
      toast.success(active ? 'Connector activated' : 'Connector deactivated');
      qc.invalidateQueries({ queryKey: ['event-connectors'] });
    },
    onError: () => toast.error('Failed to update connector'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => eventConnectorApi.delete(id),
    onSuccess: () => {
      toast.success('Connector deleted');
      setSelected(null);
      qc.invalidateQueries({ queryKey: ['event-connectors'] });
    },
    onError: () => toast.error('Failed to delete connector'),
  });

  const testMutation = useMutation({
    mutationFn: (id: string) => eventConnectorApi.test(id),
    onSuccess: (res) => {
      if (res.inbound?.success)  toast.success(`Test passed — ${res.inbound.workflowsTriggered} workflow(s) triggered`);
      if (res.outbound?.success) toast.success('Test published successfully');
      if (!res.inbound?.success && !res.outbound?.success) toast.error('Test failed');
    },
    onError: () => toast.error('Test failed'),
    onSettled: () => setTestingId(null),
  });

  return (
    <div className={styles.page}>
      <PageGuide pageKey="eventConnectors" />

      {/* ── Header ── */}
      <div className={styles.header}>
        <div>
          <h1 className={styles.title}><Network size={22} /> Event Connectors</h1>
          <p className={styles.subtitle}>
            Bridge enska.ai to external messaging systems — Pub/Sub, Kafka, SQS, SNS, EventBridge, Azure Service Bus, or plain webhooks
          </p>
        </div>
        <button id="new-connector-btn" className={styles.primaryBtn} onClick={() => setShowCreate(true)}>
          <Plus size={15} /> New Connector
        </button>
      </div>

      {/* ── Provider legend ── */}
      <div className={styles.legend}>
        {(Object.entries(PROVIDER_META) as [EventConnectorProvider, typeof PROVIDER_META[EventConnectorProvider]][]).map(([key, meta]) => (
          <span key={key} className={styles.legendItem} style={{ '--dot': meta.color } as React.CSSProperties}>
            {meta.icon} {meta.label}
          </span>
        ))}
      </div>

      {isLoading && <div className={styles.empty}><Spinner size="md" /></div>}

      {!isLoading && connectors.length === 0 && (
        <div className={styles.empty}>
          <Network size={40} className={styles.emptyIcon} />
          <p style={{ margin: '12px 0 4px', fontSize: 15, color: '#94a3b8' }}>No event connectors yet</p>
          <p style={{ margin: 0, fontSize: 13, color: '#475569' }}>Create one to start routing external events into your workflows.</p>
          <button className={styles.primaryBtn} style={{ marginTop: 20 }} onClick={() => setShowCreate(true)}>
            <Plus size={14} /> Create your first connector
          </button>
        </div>
      )}

      {!isLoading && connectors.length > 0 && (
        <div className={styles.grid}>
          {connectors.map((c) => {
            const pm = PROVIDER_META[c.provider];
            const dm = DIRECTION_META[c.direction];
            return (
              <div key={c.id} className={styles.card}>
                <div className={styles.cardHeader}>
                  <span className={styles.providerBadge} style={{ '--c': pm.color } as React.CSSProperties}>
                    {pm.icon} {pm.label}
                  </span>
                  <button
                    onClick={() => toggleMutation.mutate({ id: c.id, active: !c.active })}
                    className={styles.statusPill}
                    style={{ '--dot-color': c.active ? '#10b981' : '#64748b' } as React.CSSProperties}
                  >
                    <Circle size={6} fill="currentColor" />
                    {c.active ? 'Active' : 'Inactive'}
                  </button>
                </div>

                <h3 className={styles.cardTitle}>{c.name}</h3>
                {c.description && <p className={styles.cardDesc}>{c.description}</p>}

                <div className={styles.cardMeta}>
                  <span className={styles.dirBadge} style={{ '--c': dm.color } as React.CSSProperties}>
                    {dm.icon} {dm.label}
                  </span>
                  <span className={styles.wfCount}>
                    {c._count?.workflowLinks ?? 0} workflow{(c._count?.workflowLinks ?? 0) !== 1 ? 's' : ''} linked
                  </span>
                </div>

                {c.webhookSecret && (
                  <div className={styles.secretRow}>
                    <span>Secret:</span>
                    <code>{c.webhookSecret.slice(0, 8)}••••••••</code>
                  </div>
                )}

                <div className={styles.cardFooter}>
                  <button
                    className={styles.testBtn}
                    onClick={() => { setTestingId(c.id); testMutation.mutate(c.id); }}
                    disabled={testMutation.isPending && testingId === c.id}
                    title="Send a test event through this connector"
                  >
                    {testMutation.isPending && testingId === c.id
                      ? <Spinner size="sm" />
                      : <><FlaskConical size={13} /> Test</>
                    }
                  </button>
                  <button className={styles.configBtn} onClick={() => setSelected(c)}>
                    Configure <ChevronRight size={13} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && (
        <CreateConnectorDrawer
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); qc.invalidateQueries({ queryKey: ['event-connectors'] }); }}
        />
      )}

      {selected && (
        <ConnectorDetailDrawer
          connector={selected}
          onClose={() => setSelected(null)}
          onDeleted={() => deleteMutation.mutate(selected.id)}
          onToggle={() => toggleMutation.mutate({ id: selected.id, active: !selected.active })}
          onTest={() => { setTestingId(selected.id); testMutation.mutate(selected.id); }}
          isTesting={testMutation.isPending && testingId === selected.id}
        />
      )}
    </div>
  );
}

// ── Create drawer ──────────────────────────────────────────────────────────────

function CreateConnectorDrawer({ onClose, onCreated }: {
  onClose: () => void;
  onCreated: () => void;
}) {
  const [name, setName]           = useState('');
  const [description, setDesc]    = useState('');
  const [provider, setProvider]   = useState<EventConnectorProvider>('WEBHOOK');
  const [direction, setDirection] = useState<EventConnectorDirection>('INBOUND');
  const [fieldVals, setFieldVals] = useState<Record<string, string | boolean>>({});
  const [schemaText, setSchemaText] = useState('');
  const [schemaError, setSchemaError] = useState<string | null>(null);

  const setV = (key: string, val: string | boolean) =>
    setFieldVals((prev) => ({ ...prev, [key]: val }));

  const handleProviderChange = (p: EventConnectorProvider) => {
    setProvider(p);
    setFieldVals({});
  };

  const handleSchemaChange = (val: string) => {
    setSchemaText(val);
    if (!val.trim()) {
      setSchemaError(null);
      return;
    }
    try {
      JSON.parse(val);
      setSchemaError(null);
    } catch {
      setSchemaError('Invalid JSON format');
    }
  };

  const fields = PROVIDER_FIELDS[provider];

  const { mutate, isPending } = useMutation({
    mutationFn: () => {
      const config = buildConfig(fields, fieldVals);
      let parsedSchema = null;
      if (schemaText.trim()) {
        try {
          parsedSchema = JSON.parse(schemaText);
        } catch {
          toast.error('Invalid JSON Schema format');
          throw new Error('Invalid JSON');
        }
      }
      return eventConnectorApi.create({ name, description, provider, direction, config, schema: parsedSchema });
    },
    onSuccess: () => { toast.success('Connector created'); onCreated(); },
    onError: (e: Error) => toast.error(e.message || 'Failed to create connector'),
  });

  const requiredFilled = fields
    .filter((f) => f.required)
    .every((f) => {
      const v = fieldVals[f.key];
      if (f.type === 'toggle') return true;
      return typeof v === 'string' && v.trim().length > 0;
    });

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <h2>New Event Connector</h2>
          <button onClick={onClose} className={styles.closeBtn}>×</button>
        </div>

        <Row label="Name" required>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Order Events — Pub/Sub" autoFocus style={inputStyle} />
        </Row>
        <Row label="Description">
          <input value={description} onChange={(e) => setDesc(e.target.value)} placeholder="What does this connector do?" style={inputStyle} />
        </Row>

        <Row label="Provider" required>
          <select
            value={provider}
            onChange={(e) => handleProviderChange(e.target.value as EventConnectorProvider)}
            style={{ ...inputStyle, cursor: 'pointer' }}
          >
            {(Object.keys(PROVIDER_META) as EventConnectorProvider[]).map((p) => (
              <option key={p} value={p}>{PROVIDER_META[p].label}</option>
            ))}
          </select>
        </Row>

        <Row label="Direction" required>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['INBOUND', 'OUTBOUND', 'BIDIRECTIONAL'] as const).map((d) => (
              <button
                key={d} type="button" onClick={() => setDirection(d)}
                style={{
                  flex: 1, padding: '7px 4px', borderRadius: 8, fontSize: 11, fontWeight: 700,
                  cursor: 'pointer', border: '1px solid',
                  borderColor: direction === d ? DIRECTION_META[d].color : '#1e2130',
                  background: direction === d ? `${DIRECTION_META[d].color}18` : 'transparent',
                  color: direction === d ? DIRECTION_META[d].color : '#64748b',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
                }}
              >
                {DIRECTION_META[d].icon} {d}
              </button>
            ))}
          </div>
        </Row>

        {/* ── Per-provider config fields ── */}
        <div className={styles.configSection}>
          <p className={styles.configSectionLabel}>
            <span style={{ color: PROVIDER_META[provider].color }}>{PROVIDER_META[provider].icon}</span>
            {PROVIDER_META[provider].label} Configuration
          </p>

          {fields.map((f) => (
            <Row key={f.key} label={f.label} required={f.required}>
              {f.type === 'toggle' ? (
                <label className={styles.toggleRow}>
                  <div
                    className={styles.toggle}
                    data-on={fieldVals[f.key] ? 'true' : 'false'}
                    onClick={() => setV(f.key, !fieldVals[f.key])}
                    role="switch"
                    aria-checked={!!fieldVals[f.key]}
                    tabIndex={0}
                    onKeyDown={(e) => e.key === ' ' && setV(f.key, !fieldVals[f.key])}
                  >
                    <div className={styles.toggleThumb} />
                  </div>
                  <span className={styles.toggleThumb}>{fieldVals[f.key] ? 'Enabled' : 'Disabled'}</span>
                </label>
              ) : (
                <>
                  <input
                    value={(fieldVals[f.key] as string) ?? ''}
                    onChange={(e) => setV(f.key, e.target.value)}
                    placeholder={f.placeholder}
                    type={f.type === 'password' ? 'text' : f.type}
                    autoComplete="off"
                    style={{
                      ...inputStyle,
                      fontFamily: f.type === 'password' ? 'var(--font-mono, monospace)' : 'inherit',
                      fontSize: f.type === 'password' ? 12 : 13,
                    }}
                  />
                  {f.hint && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#475569', lineHeight: 1.4 }}>{f.hint}</p>}
                  {f.type === 'brokers' && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#475569' }}>Comma-separated list</p>}
                </>
              )}
            </Row>
          ))}
        </div>

        <Row label="Payload Schema (JSON Schema)">
          <textarea
            value={schemaText}
            onChange={(e) => handleSchemaChange(e.target.value)}
            placeholder='e.g. { "type": "object", "properties": { "status": { "type": "string" } } }'
            rows={4}
            style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)', fontSize: 12, resize: 'vertical' }}
          />
          {schemaError && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#ef4444' }}>{schemaError}</p>}
        </Row>

        <div style={{ display: 'flex', gap: 10, marginTop: 8 }}>
          <button onClick={onClose} style={{ flex: 1, padding: '10px', background: 'transparent', border: '1px solid #1e2130', borderRadius: 9, color: '#64748b', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            onClick={() => mutate()}
            disabled={!name.trim() || !requiredFilled || !!schemaError || isPending}
            style={{ flex: 2, padding: '10px', background: 'var(--color-primary)', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: (!name.trim() || !requiredFilled || !!schemaError) ? 0.5 : 1 }}
          >
            {isPending ? <Spinner size="sm" /> : 'Create Connector'}
          </button>
        </div>
      </div>
      <style>{`@keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

// ── Config display — labelled fields, no raw JSON ─────────────────────────────

function ConfigDisplay({ provider, config }: { provider: EventConnectorProvider; config: Record<string, unknown> }) {
  const fields = PROVIDER_FIELDS[provider];
  const rows = fields.filter((f) => {
    const v = config[f.key];
    return v !== undefined && v !== null && v !== '';
  });

  if (!rows.length) return <p style={{ color: '#475569', fontSize: 13 }}>No configuration stored.</p>;

  return (
    <div className={styles.configDisplay}>
      {rows.map((f) => {
        const val = config[f.key];
        const isSecret = SENSITIVE_KEYS.has(f.key);
        const display = Array.isArray(val)
          ? val.join(', ')
          : typeof val === 'boolean'
          ? (val ? 'Yes' : 'No')
          : String(val);

        return (
          <div key={f.key} className={styles.configRow}>
            <span className={styles.configKey}>{f.label}</span>
            <span className={styles.configVal}>{isSecret ? '••••••••••••' : display}</span>
          </div>
        );
      })}
    </div>
  );
}

// ── Detail / configure drawer ──────────────────────────────────────────────────

function ConnectorDetailDrawer({ connector, onClose, onDeleted, onToggle, onTest, isTesting }: {
  connector: EventConnector;
  onClose: () => void;
  onDeleted: () => void;
  onToggle: () => void;
  onTest: () => void;
  isTesting: boolean;
}) {
  const qc = useQueryClient();
  const pm = PROVIDER_META[connector.provider];
  const dm = DIRECTION_META[connector.direction];

  const { data: workflows = [] } = useQuery({ queryKey: ['workflows'], queryFn: workflowApi.list });
  const { data: detail } = useQuery({
    queryKey: ['event-connectors', connector.id],
    queryFn: () => eventConnectorApi.get(connector.id),
  });

  const [linkWfId, setLinkWfId]           = useState('');
  const [eventFilter, setEventFilter]     = useState('');
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [schemaText, setSchemaText]       = useState('');
  const [schemaError, setSchemaError]     = useState<string | null>(null);

  const isSchemaModified = schemaText !== (detail?.schema ? JSON.stringify(detail.schema, null, 2) : '');

  useEffect(() => {
    setSchemaText(detail?.schema ? JSON.stringify(detail.schema, null, 2) : '');
    setSchemaError(null);
  }, [detail?.schema]);

  const handleSchemaChange = (val: string) => {
    setSchemaText(val);
    if (!val.trim()) {
      setSchemaError(null);
      return;
    }
    try {
      JSON.parse(val);
      setSchemaError(null);
    } catch {
      setSchemaError('Invalid JSON format');
    }
  };

  const updateSchemaMutation = useMutation({
    mutationFn: (newSchema: any) => eventConnectorApi.update(connector.id, { schema: newSchema }),
    onSuccess: () => {
      toast.success('Schema updated');
      qc.invalidateQueries({ queryKey: ['event-connectors'] });
      qc.invalidateQueries({ queryKey: ['event-connectors', connector.id] });
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to update schema'),
  });

  const handleSaveSchema = () => {
    if (schemaError) return;
    let parsed = null;
    if (schemaText.trim()) {
      try {
        parsed = JSON.parse(schemaText);
      } catch {
        setSchemaError('Invalid JSON format');
        return;
      }
    }
    updateSchemaMutation.mutate(parsed);
  };

  const linkMutation = useMutation({
    mutationFn: () => eventConnectorApi.linkWorkflow(connector.id, { workflowId: linkWfId, eventFilter: eventFilter || undefined }),
    onSuccess: () => {
      toast.success('Workflow linked');
      setLinkWfId(''); setEventFilter('');
      qc.invalidateQueries({ queryKey: ['event-connectors', connector.id] });
      qc.invalidateQueries({ queryKey: ['event-connectors'] });
    },
    onError: (e: any) => toast.error(e?.message || 'Failed to link workflow'),
  });

  const unlinkMutation = useMutation({
    mutationFn: (wfId: string) => eventConnectorApi.unlinkWorkflow(connector.id, wfId),
    onSuccess: () => {
      toast.success('Workflow unlinked');
      qc.invalidateQueries({ queryKey: ['event-connectors', connector.id] });
      qc.invalidateQueries({ queryKey: ['event-connectors'] });
    },
    onError: () => toast.error('Failed to unlink workflow'),
  });

  const links = detail?.workflowLinks ?? [];
  const linkedIds = new Set(links.map((l) => l.workflowId));
  const unlinkedWfs = workflows.filter((w) => !linkedIds.has(w.id));

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(4px)', zIndex: 40 }} />
      <div className={styles.drawer}>
        <div className={styles.drawerHeader}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ color: pm.color }}>{pm.icon}</span>
            <h2 style={{ margin: 0 }}>{connector.name}</h2>
          </div>
          <button onClick={onClose} className={styles.closeBtn}>×</button>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
          <button onClick={onToggle} className={styles.statusPill} style={{ '--dot-color': connector.active ? '#10b981' : '#64748b' } as React.CSSProperties}>
            <Circle size={6} fill="currentColor" />{connector.active ? 'Active' : 'Inactive'}
          </button>
          <span className={styles.dirBadge} style={{ '--c': dm.color } as React.CSSProperties}>{dm.icon} {dm.label}</span>
          <span className={styles.providerBadge} style={{ '--c': pm.color } as React.CSSProperties}>{pm.icon} {pm.label}</span>
        </div>

        {connector.description && (
          <p style={{ color: '#64748b', fontSize: 13, margin: '0 0 20px' }}>{connector.description}</p>
        )}

        {connector.webhookSecret && (
          <div className={styles.secretBox}>
            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Webhook Signing Secret</p>
            <code style={{ fontSize: 12, color: '#22d3ee', wordBreak: 'break-all' }}>{connector.webhookSecret}</code>
            <p style={{ margin: '8px 0 0', fontSize: 11, color: '#475569' }}>
              Use <code>X-Enska-Signature</code> header (HMAC-SHA256) to verify inbound requests.
            </p>
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Configuration</p>
          <ConfigDisplay provider={connector.provider} config={connector.config} />
        </div>

        {detail?.lastError && (
          <div style={{ padding: '12px 14px', background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.25)', borderRadius: 8, marginBottom: 20 }}>
            <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 700, color: '#fca5a5', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Last Ingest Error</p>
            <p style={{ margin: 0, fontSize: 12, color: '#f87171', whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}>{detail.lastError}</p>
          </div>
        )}

        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Payload Schema (JSON Schema)</p>
            {isSchemaModified && (
              <button 
                onClick={handleSaveSchema} 
                disabled={!!schemaError || updateSchemaMutation.isPending}
                style={{ padding: '3px 8px', background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}
              >
                {updateSchemaMutation.isPending ? 'Saving...' : 'Save Schema'}
              </button>
            )}
          </div>
          <textarea
            value={schemaText}
            onChange={(e) => handleSchemaChange(e.target.value)}
            placeholder='e.g. { "type": "object", "properties": { "status": { "type": "string" } } }'
            rows={5}
            style={{ ...inputStyle, fontFamily: 'var(--font-mono, monospace)', fontSize: 12, resize: 'vertical' }}
          />
          {schemaError && <p style={{ margin: '4px 0 0', fontSize: 11, color: '#ef4444' }}>{schemaError}</p>}
        </div>

        {(connector.direction === 'INBOUND' || connector.direction === 'BIDIRECTIONAL') && (
          <div style={{ marginBottom: 20 }}>
            <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Linked Workflows ({links.length})
            </p>

            {links.length === 0 && (
              <p style={{ color: '#475569', fontSize: 13 }}>No workflows linked. Link one below to trigger it on inbound events.</p>
            )}

            {links.map((l) => (
              <div key={l.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', background: '#0a0d1a', borderRadius: 8, border: '1px solid #1e2130', marginBottom: 6 }}>
                <Zap size={12} color="#6366f1" />
                <span style={{ flex: 1, fontSize: 13, color: '#e2e8f0' }}>{l.workflow?.name ?? l.workflowId}</span>
                {l.eventFilter && <code style={{ fontSize: 10, color: '#64748b', background: '#0f1221', padding: '2px 6px', borderRadius: 4 }}>{l.eventFilter}</code>}
                <button onClick={() => unlinkMutation.mutate(l.workflowId)} disabled={unlinkMutation.isPending}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#475569', padding: 2 }} title="Unlink">
                  <Trash2 size={13} />
                </button>
              </div>
            ))}

            {unlinkedWfs.length > 0 && (
              <div style={{ marginTop: 12 }}>
                <select value={linkWfId} onChange={(e) => setLinkWfId(e.target.value)} style={{ ...inputStyle, marginBottom: 6 }}>
                  <option value="">Select workflow to link…</option>
                  {unlinkedWfs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
                <div style={{ display: 'flex', gap: 6 }}>
                  <input
                    value={eventFilter} onChange={(e) => setEventFilter(e.target.value)}
                    placeholder="Event filter: event.type=order.created (optional)"
                    style={{ ...inputStyle, flex: 1, fontSize: 11 }}
                  />
                  <button onClick={() => linkMutation.mutate()} disabled={!linkWfId || linkMutation.isPending}
                    style={{ padding: '8px 16px', background: 'var(--color-primary)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', opacity: !linkWfId ? 0.5 : 1 }}>
                    {linkMutation.isPending ? <Spinner size="sm" /> : '+ Link'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, paddingTop: 12, borderTop: '1px solid #1e2130' }}>
          <button onClick={onTest} disabled={isTesting}
            style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px', background: '#0f1b30', border: '1px solid #1e3a5f', borderRadius: 9, color: '#38bdf8', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {isTesting ? <Spinner size="sm" /> : <><FlaskConical size={14} /> Test</>}
          </button>
          {!confirmDelete ? (
            <button onClick={() => setConfirmDelete(true)}
              style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: '9px', background: '#1c0a0a', border: '1px solid #4c1414', borderRadius: 9, color: '#f87171', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
              <Trash2 size={14} /> Delete
            </button>
          ) : (
            <button onClick={onDeleted}
              style={{ flex: 1, padding: '9px', background: '#ef4444', border: 'none', borderRadius: 9, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
              Confirm Delete
            </button>
          )}
        </div>
      </div>
      <style>{`@keyframes drawerIn { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
    </>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

function Row({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#94a3b8', marginBottom: 6 }}>
        {label} {required && <span style={{ color: '#f87171' }}>*</span>}
      </label>
      {children}
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '9px 12px', background: '#0f1221', border: '1px solid #1e2130',
  borderRadius: 8, color: '#e2e8f0', fontSize: 13, outline: 'none', boxSizing: 'border-box',
};
